import * as React from "react";
import { PortRoute } from "./port_route";
import { WorkloadSubdomainExactlyOneRouteCheck } from "./workloadSubdomain_exactlyOneRoute_check";
import { PortRouteEditHeadersModal } from "../../routeEditHeadersModal";
import { PortRouteEditMirrorsModal } from "../../routeEditMirrorsModal";
import { getDefaultDomainPortRouteInput } from "../utils/utils";
import { Alert, Button } from "antd";
import { formSet, formSetValue, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { cloneDeep } from "lodash";
import { CpFormItem } from "../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { CpErrorWarningArray } from "../../../gvc/testNewForm/CpErrorWarningArray";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { parseLink } from "../../../../utils/linkParser/linkParser";

interface Props {
  index: number;
}
export const PortRoutes: React.FC<Props> = ({ index }) => {
  const { id } = useCpFormContext();

  const [isEditingRouteHeaders, setIsEditingRouteHeaders] = React.useState(false);
  const [isEditingRouteMirrors, setIsEditingRouteMirrors] = React.useState(false);

  const routingMode = useCpFormValue("advanced.spec.routingMode");

  const gvcLink = useCpFormValue("gvc.link");
  const gvcName = React.useMemo(() => {
    const parsedLink = parseLink(gvcLink, { useInputCtx: true });
    return parsedLink.name;
  }, [gvcLink]);
  const acceptAll = useCpFormValue("advanced.spec.acceptAll");
  const routes = useCpFormValue(`advanced.spec.ports.${index}.routes`);

  function addRoute() {
    const _routes = cloneDeep(routes);
    _routes.push({
      ...getDefaultDomainPortRouteInput(),
      prefix: `/`,
    });
    formSetValue(id, `advanced.spec.ports.${index}.routes`, _routes);

    formSet(id, `fieldStates.advanced.spec.ports.${index}.routesroot.simulateTouched`, true);
    for (const routeIndex in _routes) {
      formSet(id, `fieldStates.advanced.spec.ports.${index}.routes.${routeIndex}.prefix.simulateTouched`, true);
      formSet(id, `fieldStates.advanced.spec.ports.${index}.routes.${routeIndex}.regex.simulateTouched`, true);
      formSet(id, `fieldStates.advanced.spec.ports.${index}.routes.${routeIndex}.replacePrefix.simulateTouched`, true);
      formSet(id, `fieldStates.advanced.spec.ports.${index}.routes.${routeIndex}.workload.simulateTouched`, true);
      formSet(id, `fieldStates.advanced.spec.ports.${index}.routes.${routeIndex}.port.simulateTouched`, true);
      formSet(id, `fieldStates.advanced.spec.ports.${index}.routes.${routeIndex}.hostPrefix.simulateTouched`, true);
      formSet(id, `fieldStates.advanced.spec.ports.${index}.routes.${routeIndex}.hostRegex.simulateTouched`, true);
      formSet(id, `fieldStates.advanced.spec.ports.${index}.routes.${routeIndex}.replica.simulateTouched`, true);
      formSet(id, `fieldStates.advanced.spec.ports.${index}.routes.${routeIndex}.headersRoot.simulateTouched`, true);
      formSet(id, `fieldStates.advanced.spec.ports.${index}.routes.${routeIndex}.mirrorroot.simulateTouched`, true);
    }
  }

  // 1 type - all
  // 2 prefix/regex - all
  // 2 replacePrefix - all
  // 1 type (only when either acceptAllHosts or acceptAllSubdomains are enabled)
  // 2 hostPrefix/hostRegex (only when either acceptAllHosts or acceptAllSubdomains are enabled)
  // 2 workloadLink - pathBased
  // 1 port - all
  // 1 replica - workloadSubdomain

  const colCount = React.useMemo(() => {
    let val = 0;
    if (routingMode === "pathBased") {
      val = 8;
    }
    if (routingMode === "workloadSubdomain") {
      val = 7;
    }
    if (acceptAll !== "none") {
      val += 3;
    }
    return val;
  }, [routingMode, acceptAll]);

  if (routingMode === "gvcSubdomain") {
    return null;
  }

  if (routingMode === "pathBased" && !gvcName) {
    return (
      <div key={`port-routes-${index}`}>
        <CpFormItem>
          <CpLabel name={`skip`}>Routes</CpLabel>
          <Alert
            showIcon
            type={"info"}
            className="mb-4 w-fit"
            message={`GVC Selection is needed for configuring routes.`}
          />
        </CpFormItem>
      </div>
    );
  }

  return (
    <>
      <div key={`port-routes-${index}`} className={`mb-12`}>
        <div className="flex items-center gap-2 mb-2">
          <div className="cursor-default">Routes</div>
          <Button type="primary" size={`small`} onClick={addRoute}>
            Add
          </Button>
          {/* // TODO can show error icon here for headers */}
          <Button className="ml-2" size={"small"} type="default" onClick={() => setIsEditingRouteHeaders(true)}>
            Edit Headers
          </Button>
          <Button className="ml-2" size={"small"} type="default" onClick={() => setIsEditingRouteMirrors(true)}>
            Edit Mirrors
          </Button>
        </div>
        {routes.length > 0 ? (
          <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}>
            <div className="col-span-1">Type</div>
            <div className="col-span-2">Prefix/Regex</div>
            <div className="col-span-2">Replace Prefix</div>
            {acceptAll !== "none" ? (
              <>
                <div className="col-span-1">Type</div>
                <div className="col-span-2">Host Prefix/Host Regex</div>
              </>
            ) : null}
            {routingMode === "pathBased" ? (
              <div className="col-span-2">
                <div className="flex items-center">
                  <span>Workload Link</span>
                  <span className="color-danger">*</span>
                </div>
              </div>
            ) : null}
            <div className="col-span-1">Port</div>
            {routingMode === "pathBased" ? null : routingMode === "workloadSubdomain" ? (
              <div className="col-span-1">Replica</div>
            ) : null}
          </div>
        ) : (
          <div>No Routes</div>
        )}
        {routes.map((r) => (
          <PortRoute key={r.id} portIndex={index} routeId={r.id} />
        ))}
        <WorkloadSubdomainExactlyOneRouteCheck />
        <CpErrorWarningArray
          names={[
            `advanced.spec.ports.${index}.routesroot`,
            ...routes.map((_, i) => `advanced.spec.ports.${index}.routes.${i}`),
            ...routes.map((_, i) => `advanced.spec.ports.${index}.routes.${i}.prefix`),
            ...routes.map((_, i) => `advanced.spec.ports.${index}.routes.${i}.regex`),
            ...routes.map((_, i) => `advanced.spec.ports.${index}.routes.${i}.replacePrefix`),
            ...routes.map((_, i) => `advanced.spec.ports.${index}.routes.${i}.workload`),
            ...routes.map((_, i) => `advanced.spec.ports.${index}.routes.${i}.port`),
            ...routes.map((_, i) => `advanced.spec.ports.${index}.routes.${i}.hostPrefix`),
            ...routes.map((_, i) => `advanced.spec.ports.${index}.routes.${i}.hostRegex`),
            ...routes.map((_, i) => `advanced.spec.ports.${index}.routes.${i}.replica`),
            ...routes.map((_, i) => `advanced.spec.ports.${index}.routes.${i}.headersRoot`),
            ...routes.map((_, i) => `advanced.spec.ports.${index}.routes.${i}.mirrorroot`),
            ...routes.flatMap((r, i) =>
              (r.mirror || []).flatMap((_, mi) => [
                `advanced.spec.ports.${index}.routes.${i}.mirror.${mi}.workload`,
                `advanced.spec.ports.${index}.routes.${i}.mirror.${mi}.percent`,
                `advanced.spec.ports.${index}.routes.${i}.mirror.${mi}.port`,
              ]),
            ),
          ]}
        />
      </div>
      {isEditingRouteHeaders ? (
        <PortRouteEditHeadersModal
          portIndex={index}
          onClose={() => {
            setIsEditingRouteHeaders(false);
            const newRoutes = routes.map((r) => ({
              ...r,
              headers: r.headers.filter((h) => !!h.name),
            }));
            formSetValue(id, `advanced.spec.ports.${index}.routes`, newRoutes);
          }}
        />
      ) : null}
      {isEditingRouteMirrors ? (
        <PortRouteEditMirrorsModal
          portIndex={index}
          onClose={() => {
            setIsEditingRouteMirrors(false);
            const newRoutes = routes.map((r) => ({
              ...r,
              mirror: (r.mirror || []).filter((m) => !!m.workload.value),
            }));
            formSetValue(id, `advanced.spec.ports.${index}.routes`, newRoutes);
          }}
        />
      ) : null}
    </>
  );
};
